import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { api, can, isActive, type Run } from '../api'
import { RunStatus } from '../components'
import { DataGrid, type BulkAction, type GridFilter } from '../DataGrid'
import { dt, elapsed, num } from '../format'
import { pollWhile, useNow } from '../hooks'

const opts = (...v: string[]) => v.map((x) => ({ value: x, label: x }))

const runFilters: GridFilter<Run>[] = [
  { key: 'status', label: 'Status', options: opts('queued', 'running', 'succeeded', 'failed', 'skipped', 'cancelled'), value: (r) => r.status },
  { key: 'kind', label: 'Kind', options: opts('ingest', 'build', 'extract', 'preview', 'test', 'maintenance'), value: (r) => r.kind },
  { key: 'trigger', label: 'Trigger', options: opts('schedule', 'manual', 'upstream'), value: (r) => r.trigger },
]

export function Runs() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const editor = can(me.data, 'editor')
  const actions = useMemo<BulkAction<Run>[]>(
    () => (editor ? [{ label: 'Cancel', applies: isActive, confirm: 'Cancel these runs?', run: (r) => api.cancelRun(r.id) }] : []),
    [editor],
  )
  const now = useNow(1000)
  const q = useQuery({
    queryKey: ['runs'],
    queryFn: () => api.runs({ limit: 500 }),
    refetchInterval: (query) => pollWhile(!!query.state.data?.some(isActive)),
  })

  const cols = useMemo<ColDef<Run>[]>(
    () => [
      { field: 'id', headerName: '#', width: 80, cellClass: 'mono', sort: 'desc' },
      { field: 'kind', width: 110 },
      { headerName: 'Target', width: 190, valueGetter: (p) => `${p.data?.target_kind}/${p.data?.target_slug}`, cellClass: 'mono' },
      {
        field: 'status',
        flex: 2,
        minWidth: 260,
        cellRenderer: ({ data }: ICellRendererParams<Run>) => (data ? <RunStatus run={data} /> : null),
      },
      { field: 'trigger', width: 100 },
      { field: 'created_by', headerName: 'By', width: 150 },
      { field: 'created_at', headerName: 'Created', width: 140, valueFormatter: (p) => dt(p.value) },
      { headerName: 'Duration', width: 120, valueGetter: (p) => (p.data ? elapsed(p.data.started_at, p.data.finished_at, now) : '') },
      { field: 'rows_in', headerName: 'Rows in', width: 120, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { field: 'rows_out', headerName: 'Rows out', width: 120, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { field: 'error', flex: 1, minWidth: 200, tooltipField: 'error', cellClass: 'err-t' },
    ],
    [now],
  )

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Runs</h1>
          <p className="sub">Ingest, build and maintenance runs. The worker runs one at a time; queued runs wait their turn.</p>
        </div>
      </header>
      {q.error && <p className="callout err">{q.error.message}</p>}
      <DataGrid<Run>
        rows={q.data}
        columnDefs={cols}
        rowHeight={42}
        pageSize={50}
        loading={q.isLoading}
        getRowId={(r) => String(r.id)}
        searchText={(r) => [r.id, r.kind, r.target_kind, r.target_slug, r.status, r.created_by, r.error].join(' ')}
        filters={runFilters}
        actions={actions}
        onDone={() => qc.invalidateQueries({ queryKey: ['runs'] })}
        onOpen={(r) => navigate(`/runs/${r.id}`)}
      />
    </section>
  )
}
